import { ALLOWED_BANKS_BACKEND, getBankDisplayName } from 'enums';
import moment from 'moment';
import {
  BusinessGoalsRequest,
  BusinessProjectedGrossIncome,
  BusinessProjectedBusinessExpense,
  CompanyApi,
  CompanySetupState,
  OwnerRequestData,
  Owner,
  UpdateCompanySetupState,
  UpdateIncorporationStatusSetting,
} from 'models/company';
import { Business } from 'models/business';
import { ProgressTrackerGroupRequest } from 'models/account';
import { CoverLetterData } from 'hooks/api';
import { queryClient } from 'states/reactQueryClient';
import { apiV2 } from './axios';

export const transformCompany = (company: CompanyApi): Business =>
  ({
    ...company,
    bankName: getBankDisplayName(company.bankName ?? '') ?? company.bankName,
  } as Business);
// omitBy(
//   {
//     ...company,
//     bankName: getBankDisplayName(company.bankName ?? '') ?? company.bankName,
//   } as Business,
//   isNil,
// );

export const getCompanyById = async (id: string): Promise<Business> => {
  const { data } = await apiV2.get<CompanyApi>(`businesses/${id}`);
  return transformCompany(data);
};

export const getCompanyListByUserId = async (
  userId?: string,
): Promise<Business[]> => {
  const { data } = await apiV2.get<Business[]>(`/businesses/by-user/${userId}`);
  return data;
};

// export const getCompanyListByUserId = async (
//   userId?: string,
// ): Promise<Business[]> => {
//   const { data } = await apiV2.get<{ data: CompanyApi[] }>(
//     `users/${userId}/companies`,
//   );

//   return data.data.map(transformCompany);
// };

export const transformCompanyDataForUpdate = (params: Partial<Business>) => {
  const newParams: CompanyApi = { ...params } as CompanyApi;
  if (newParams.incorporationDate) {
    newParams.incorporationDate = moment(params.incorporationDate).format(
      'MM/DD/YYYY',
    );
  }
  if (newParams.incorporationRenewalDate) {
    newParams.incorporationRenewalDate = moment(
      params.incorporationRenewalDate,
    ).format('MM/DD/YYYY');
  }
  if (!newParams.ein) {
    delete newParams.ein;
  }
  if (newParams.bankName) {
    newParams.bankName =
      ALLOWED_BANKS_BACKEND[newParams.bankName] || newParams.bankName;
  }
  return newParams;
};

export const createCompanyByUserId = async (
  userId: string,
  params: Business,
) => {
  const newParams = transformCompanyDataForUpdate(params);
  await apiV2.post(`users/${userId}/companies`, newParams);
};

export const upsertCompanyByUserId = async (
  userId: string,
  params: Partial<Business>,
) => {
  const param = transformCompanyDataForUpdate(params);
  const { data } = await apiV2.post(`users/${userId}/companies`, param);
  return data;
};

export const createCompany = async (params: Partial<Business>) => {
  const { data } = await apiV2.post(`businesses`, params);
  return data;
};

export const updateCompanyById = async (
  companyId: string,
  params: Partial<Business>,
) => {
  const param = transformCompanyDataForUpdate(params);
  const { data } = await apiV2.patch(`businesses/${companyId}`, param);
  return data;
};

export const updateCompanyProgressById = async (
  companyId: string,
  params: { progress: ProgressTrackerGroupRequest[] },
) => {
  const { data } = await apiV2.put(`businesses/${companyId}/progress`, params);
  return data;
};

export const updateIncorporationStatus = async (
  id: string,
  payload: UpdateIncorporationStatusSetting,
) => {
  const { data } = await apiV2.patch(`businesses/${id}/incorporation-status`, {
    ...payload,
  });
  return data;
};

export type { Business };

export const initIncorporationStatus = async (id: string) => {
  const { data } = await apiV2.post(`businesses/${id}/incorporation-status`);
  return data;
};

export const updateCompanyCoverLetter = async (
  companyId: string,
  params: CoverLetterData,
) => apiV2.post(`businesses/${companyId}/cover-letter`, params);

export const getCompanyCoverLetter = async (
  companyId: string,
): Promise<CoverLetterData> => {
  const { data } = await apiV2.get<CoverLetterData>(
    `businesses/${companyId}/cover-letter`,
  );
  return data;
};

export const activateBusiness = async (businessId: string) => {
  const { data } = await apiV2.post<{ data: Business }>(
    `businesses/${businessId}/activate`,
  );
  return data;
};

export const notifyIncorporationStatus = async (companyId: string) => {
  await apiV2.post(`businesses/${companyId}/notify-incorporation`);
};

export const createOwner = async (
  businessId: string,
  params: OwnerRequestData,
) => {
  const param = { ...params };
  if (param.dob) {
    param.dob = moment(params.dob).format('MM/DD/YYYY');
  }
  await apiV2.post(`businesses/${businessId}/owner`, param);
};

export const updateOwner = async (
  ownerId: string,
  businessId: string,
  params: Partial<OwnerRequestData>,
) => {
  const param = { ...params };
  if (param.dob) {
    param.dob = moment(params.dob).format('MM/DD/YYYY');
  }
  await apiV2.post(`businesses/${businessId}/owner/${ownerId}`, param);
};

export const deleteOwner = async (ownerId: string, businessId: string) => {
  await apiV2.delete(`businesses/${businessId}/owner/${ownerId}`);
};

export const getOwners = async (businessId: string): Promise<Owner[]> => {
  const { data } = await apiV2.get<Owner[]>(`businesses/${businessId}/owner`);
  return data;
};

export const getOwner = async (
  ownerId: string,
  businessId: string,
  viewSSN: boolean = false,
): Promise<Owner> => {
  const { data } = await apiV2.get<Owner>(
    `businesses/${businessId}/owner/${ownerId}${
      viewSSN ? '?viewssn=true' : ''
    }`,
  );
  return data;
};

export const getCompanySetupState = async (companyId: string) => {
  try {
    const { data } = await apiV2.get<CompanySetupState>(
      `businesses/${companyId}/setup-status`,
    );
    return data;
  } catch (error) {
    console.error('Error getting company setup state', error);
    return undefined;
  }
};

export const setCompanySetupState = async (
  companyId: string,
  state: Partial<UpdateCompanySetupState>,
) => {
  const { data } = await apiV2.post<Partial<UpdateCompanySetupState>>(
    `businesses/${companyId}/setup-status`,
    state,
  );
  await queryClient.invalidateQueries(['companySetupState']);
  return data;
};

export const updateBusinessGoals = async (
  companyId: string,
  params: Partial<BusinessGoalsRequest>,
) => apiV2.post(`businesses/${companyId}/goals`, params);

export const getBusinessProjectedGrossIncome = async (
  businessId: string,
  year: number,
) => {
  const { data } = await apiV2.get<BusinessProjectedGrossIncome>(
    `businesses/${businessId}/projected-gross-income`,
    {
      params: {
        year,
      },
    },
  );
  return data;
};

export const getProjectedBusinessExpense = async (
  businessId: string,
  year: number,
) => {
  const { data } = await apiV2.get<BusinessProjectedBusinessExpense>(
    `businesses/${businessId}/projected-expense`,
    {
      params: {
        year,
      },
    },
  );
  return data;
};
